import { FavouriteSearchBar } from 'app/src/features/Favourites/components/FavouriteSearchBar/FavouriteSearchBar'
import { companyagreementApi } from 'app/src/services/welfareportal'
import type { ProductListItem } from 'app/src/services/welfareportal/companyagreement-api'
import { BreadCrumbClickableItemTemplate, PageWidget } from 'app/src/shared/components'
import { ProductSubcategoryCard } from 'app/src/shared/components/ProductSubcategoryCard/ProductSubcategoryCard'
import { usePageTitle, useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import { useDebounce } from 'primereact/hooks'
import type { MenuItem } from 'primereact/menuitem'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export function Favourites(): ReactElement {
  const context = useWebAppContext()
  const navigate = useWelfarePortalNavigate()
  const appBarT = useTranslation('home', { keyPrefix: 'AppBar' })
  const { t } = useTranslation('catalog', { keyPrefix: 'Catalog.Favourites' })

  usePageTitle(t('Title'))

  const [visibleProducts, setVisibleProducts] = useState<companyagreementApi.ProductPaginated['items']>()
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<ProductListItem[]>([])
  const [searchText, debouncedSearchText, setSearchText] = useDebounce<string | undefined>(undefined, 300)
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  const productQuery = companyagreementApi.useProductList<companyagreementApi.ProductPaginated>(
    {
      queryParams: {
        onlyFavourites: true,
        pagingOffset: 0,
        pagingLimit: 5,
        key: debouncedSearchText,
      },
    },
    {
      enabled: !debouncedSearchText || debouncedSearchText.length > 2,
    },
  )

  useEffect(() => {
    if (productQuery.data?.items && isInitialLoading) {
      setIsInitialLoading(false)
    }
  }, [productQuery.data?.items, isInitialLoading])

  // Init breadcrumbs
  useEffect(() => {
    const breadCrumbItems: MenuItem[] = [
      {
        template: () =>
          BreadCrumbClickableItemTemplate({
            label: appBarT.t('Catalog'),
            command: () => navigate.goToCatalog(),
          }),
      },
      {
        template: () =>
          BreadCrumbClickableItemTemplate({
            label: t('BreadcrumbLabel'),
          }),
      },
    ]

    context.update({ breadCrumbItems })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize product visibility and suggestions
  useEffect(() => {
    if (productQuery.data?.items && !debouncedSearchText) {
      setVisibleProducts(productQuery.data.items)
    } else if (productQuery.data?.items && debouncedSearchText) {
      setAutoCompleteSuggestions(productQuery.data.items)
    }
  }, [productQuery.data?.items, debouncedSearchText])

  const handleProductSelect = useCallback(
    (evt: { value: ProductListItem }) => {
      navigate.goToShowcase({ selectedProduct: evt.value.productId, favourites: true })
    },
    [navigate],
  )

  const handleSearchClick = () => {
    if (productQuery.data?.items) {
      setVisibleProducts([...productQuery.data.items])
      setAutoCompleteSuggestions([])
    }
  }

  return (
    <PageWidget>
      <h2 className='mb-4 text-2xl font-bold'>{t('Title')}</h2>
      <div className='mb-8'>{t('Description')}</div>

      {!isInitialLoading ? (
        <>
          <FavouriteSearchBar
            autoCompleteSearchText={searchText}
            suggestions={autoCompleteSuggestions}
            isLoading={productQuery.isLoading}
            onSearchTextChange={(text) => setSearchText(text || undefined)}
            onAutoCompleteTextChange={(value) => setSearchText(value ?? '')}
            onSearchClick={handleSearchClick}
            onProductSelect={handleProductSelect}
            placeholderText={t('SearchPlaceholder')}
          />

          {visibleProducts && visibleProducts.length > 0 ? (
            <div className='cat grid grid-cols-1 gap-4 p-2 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2'>
              {visibleProducts.map((product) => (
                <ProductSubcategoryCard
                  key={product.productId}
                  mainImage={product.mainImage}
                  parentCategoryImage={product.mainImage}
                  name={product.productName ?? ''}
                  description={product.description ?? ''}
                  onClick={() => navigate.goToShowcase({ selectedProduct: product.productId, favourites: true })}
                  isFavourite={product.isFavourite}
                  isPartnership={!!product.additionalInfo?.partnership}
                />
              ))}
            </div>
          ) : (
            <div className='mt-4 text-center text-[18px] font-normal leading-6'>{t('EmptyMessage')}</div>
          )}
        </>
      ) : (
        <div className='flex w-[100%] items-center justify-center'>
          <ProgressSpinner data-testid='loading-element' className='mt-10 flex justify-center' />
        </div>
      )}
    </PageWidget>
  )
}
